import { ComponentType, LazyExoticComponent, lazy } from 'react'

// ----------------------------------------------------------------------
// A screen whose chunk is no longer on the server loads the new one.
//
// Every screen is a separate chunk with a hashed filename, and both deploys
// rsync the build with `--delete`, so the moment a deploy lands every open tab
// is holding an `index.html` that names files the server has just removed. The
// next navigation asks for one of them, gets a 404, and React throws — and
// `main` deploys to staging on every push, so an operator who keeps the console
// open all day meets this several times a week.
//
// What they met was the error boundary: "This screen could not be loaded",
// with a Reload button that fixes it every time. A dead end with a button on
// it, for a condition the application can recognise precisely and recover from
// without being asked. So it reloads itself once, and the operator sees the
// screen they asked for.
//
// **Once, and only for this.** Two things make that safe:
//
//   - The failure is identified by what the platform says when a *module* could
//     not be fetched, never by "an error happened while loading a screen". A
//     screen that throws from its own module scope — a real defect — is not a
//     missing chunk, and reloading it would replace a reported bug with a tab
//     that reloads itself forever.
//   - A reload is recorded in `sessionStorage` with the time. A second failure
//     inside the window means the reload did not fix it (the asset is genuinely
//     gone, the network is down, a proxy is serving HTML for a script), so the
//     error is rethrown and the boundary does its job. Outside the window it is
//     a new deploy and a fresh reload is right.
//
// `sessionStorage` rather than a module variable, because the thing being
// counted survives a reload and a module variable does not — one that reset on
// every load would let the loop run forever.
// ----------------------------------------------------------------------

/** Where the last automatic reload is recorded. Per tab, so two tabs do not share one. */
export const CHUNK_RELOAD_KEY = 'beast.staleChunkReloadedAt'

/**
 * How long a reload is assumed to have been the fix.
 *
 * Long enough to cover the boot of a cold console, and short enough that a
 * second deploy an hour later gets its own reload rather than an error screen.
 */
export const CHUNK_RELOAD_WINDOW_MS = 30_000

/**
 * What the four engines say when a dynamic import could not be fetched.
 *
 * Matched on the message because none of them uses a distinguishable error
 * type: all four throw a plain `TypeError`, which every failed `fetch` in the
 * application also throws. The last entry is Vite's own preload helper, which
 * fails before the import does when a `modulepreload` 404s.
 */
const CHUNK_LOAD_MESSAGES = [
  // Chrome, Edge
  'failed to fetch dynamically imported module',
  // Firefox
  'error loading dynamically imported module',
  // Safari
  'importing a module script failed',
  // WebKit, older
  'module script failed',
  // Vite's preload helper, for a stylesheet a chunk brings with it
  'unable to preload css',
]

/**
 * Whether this is a chunk that is no longer on the server, rather than a screen
 * that threw.
 *
 * Deliberately narrow. Everything not on this list reaches the error boundary
 * exactly as it did before, because the alternative — treating any failure to
 * render as a stale deploy — turns a crash an engineer could read in Sentry
 * into a tab that reloads itself and reports nothing.
 */
export const isStaleChunkError = (error: unknown): boolean => {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : ''
  const lowered = message.toLowerCase()
  return CHUNK_LOAD_MESSAGES.some((known) => lowered.includes(known))
}

interface ReloadDeps {
  /** Where the last reload is recorded. Absent — Safari in private mode — means no guard, so no reload. */
  storage: Pick<Storage, 'getItem' | 'setItem'> | null | undefined
  now: () => number
  reload: () => void
}

/**
 * Reload for a stale chunk, unless one has just been tried.
 *
 * Returns whether a reload was started, so the caller knows whether to leave
 * the promise pending (a page that is going away should not also render an
 * error) or to reject and let the boundary draw.
 */
export const reloadForStaleChunk = ({
  storage,
  now,
  reload,
}: ReloadDeps): boolean => {
  if (!storage) {
    // No way to record the attempt is no way to stop a loop, and a console that
    // reloads forever is worse than one that shows a button.
    return false
  }

  let last = 0
  try {
    last = Number(storage.getItem(CHUNK_RELOAD_KEY)) || 0
  } catch {
    // A storage that throws on read will throw on write, so treat it as absent.
    return false
  }

  const at = now()
  // `last > 0` first, and not folded into the subtraction. A tab that has
  // never reloaded holds nothing, and `at - 0` only happens to be large
  // because a wall clock is: the guard would read "reloaded just now" for any
  // clock inside the window of the epoch, which is every clock a test drives.
  if (last > 0 && at - last < CHUNK_RELOAD_WINDOW_MS) {
    return false
  }

  try {
    storage.setItem(CHUNK_RELOAD_KEY, String(at))
  } catch {
    return false
  }

  reload()
  return true
}

/** The browser's own storage, clock and reload, read lazily so a test can pass its own. */
const browserDeps = (): ReloadDeps => ({
  storage: (() => {
    try {
      return window.sessionStorage
    } catch {
      // Blocked by a cookie policy. Reading the property itself throws.
      return null
    }
  })(),
  now: () => Date.now(),
  reload: () => window.location.reload(),
})

/**
 * `React.lazy`, for a screen that lives in its own chunk.
 *
 * The only difference from `lazy` is what happens when the chunk is not there.
 * Everything else — the Suspense boundary, the error boundary, the fallback a
 * screen waits behind — is `Loadable`'s, unchanged.
 */
export const lazyScreen = <T extends ComponentType<any>>(
  load: () => Promise<{ default: T }>,
  deps: () => ReloadDeps = browserDeps,
): LazyExoticComponent<T> =>
  lazy(() =>
    load().catch((error: unknown) => {
      if (isStaleChunkError(error) && reloadForStaleChunk(deps())) {
        // The page is being replaced. Resolving here would render a screen
        // into a document that is on its way out, and rejecting would flash
        // the error boundary first — so this promise simply never settles.
        return new Promise<{ default: T }>(() => {})
      }
      throw error
    }),
  )

export default lazyScreen
